// src/pages/ForgotPassword.js
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Send, AlertCircle, ArrowLeft, Loader } from 'lucide-react'; // Import icons
import logo from "../assets/images/apemlogo.png"; // Verify logo path

// --- Helper: Inject CSS Keyframes for Spinners ---
if (!document.getElementById('spinner-keyframes')) {
    const keyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    `;
    const styleSheet = document.createElement("style");
    styleSheet.id = 'spinner-keyframes';
    styleSheet.type = "text/css";
    styleSheet.innerText = keyframes;
    document.head.appendChild(styleSheet);
}
// --- End Helper ---


export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null); // State for errors
    const [successMessage, setSuccessMessage] = useState(null); // State for success message
    const navigate = useNavigate();

    // Basic Email Validation
    const validateEmail = (emailToValidate) => {
        // More robust regex
        const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return re.test(String(emailToValidate).toLowerCase());
    };

    // Handle Forgot Password Request
    const handleForgotPassword = async () => {
        setError(null); // Clear previous errors
        setSuccessMessage(null); // Clear previous success message

        if (!email) {
            setError("Por favor, insira seu e-mail.");
            return;
        }
        if (!validateEmail(email)) {
            setError("Por favor, insira um e-mail válido.");
            return;
        }

        setLoading(true);
        console.log("ForgotPassword: Requesting password reset for:", email);

        try {
            const response = await axios.post("https://apem.riderup.uk/api/forgot-password", {
                email: email.toLowerCase().trim(), // Send trimmed and lowercase
            });

            console.log("ForgotPassword: API Response Status:", response.status);
            console.log("ForgotPassword: API Response Data:", response.data);

            // Assuming API returns 200 on success (even if email doesn't exist, for security)
            if (response.status === 200) {
                console.log("ForgotPassword: Request successful (backend received it).");
                // Show success message instead of alert before navigating
                setSuccessMessage("Se o e-mail estiver cadastrado, você receberá um link para redefinir sua senha em breve.");
                // Optionally delay navigation or wait for user action
                // setTimeout(() => navigate("/"), 3000); // Example: Navigate after 3 seconds
            } else {
                // Handle unexpected success statuses if API behaves differently
                throw new Error(response.data?.message || "Ocorreu um erro inesperado.");
            }

        } catch (error) {
            console.error("ForgotPassword: API Error:", error);
            let errorMessage = "Erro ao solicitar recuperação. Tente novamente.";
            if (axios.isAxiosError(error) && error.response) {
                errorMessage = error.response.data?.message || error.response.data?.error || `Erro ${error.response.status}`;
            } else if (error instanceof Error) {
                errorMessage = error.message;
            }
            setError(errorMessage);
            // alert(errorMessage); // Replaced by error state display

        } finally {
            // Check mount? Less critical here usually
            setLoading(false);
        }
    };

    // Handle Enter key press on email input
    const handleEmailKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleForgotPassword();
        }
    };


    return (
        <div style={styles.container}>
            {/* Logo */}
            <img src={logo} alt="Logo A Próxima é Minha" style={styles.logo} />

            {/* Title & Description */}
            <h2 style={styles.title}>Recuperar Senha</h2>
            {!successMessage && ( // Hide description if success message is shown
                <p style={styles.description}>
                    Sem problemas! Digite seu e-mail abaixo e enviaremos um link para você criar uma nova senha.
                </p>
            )}

            {/* Success Message Display */}
            {successMessage && (
                <div style={styles.successBanner}>
                    <span>{successMessage}</span>
                </div>
            )}


            {/* Email Input */}
            {!successMessage && ( // Hide form if success message is shown
                <>
                    <input
                        type="email"
                        name="email"
                        placeholder="Digite seu e-mail cadastrado"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyPress={handleEmailKeyPress} // Trigger on Enter
                        required
                        style={styles.input}
                        aria-label="Email"
                        disabled={loading}
                        autoCapitalize="none"
                        autoCorrect="off"
                    />

                    {/* Error Message Display */}
                    {error && (
                        <div style={styles.errorBanner}>
                            <AlertCircle size={18} style={{ marginRight: '8px', flexShrink: 0 }}/>
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Send Link Button */}
                    <button
                        onClick={handleForgotPassword}
                        disabled={loading}
                        style={styles.buttonPrimary(loading)}
                    >
                        {loading ? (
                            <>
                                <div style={styles.buttonSpinner}></div> Enviando...
                            </>
                        ) : (
                            <>
                                <Send size={18} /> Enviar Link de Recuperação
                            </>
                        )}
                    </button>
                </>
            )}

            {/* Back to Login Link */}
            <button
                style={styles.linkButton}
                onClick={() => navigate("/")} // Navigate back to login
                disabled={loading} // Disable while loading initial request
            >
                <ArrowLeft size={14} style={{ marginRight: '4px' }} />
                Lembrou a senha? <span style={styles.loginLink}>Entrar</span>
            </button>

            {/* Optional: Full screen loading (usually not needed here) */}
            {/* {loading && <div style={styles.loadingOverlay}><div style={styles.spinner}></div></div>} */}
        </div>
    );
}

// --- Styles ---
const styles = {
    container: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center", // Center content vertically
        backgroundColor: "#12121f", // Dark background
        minHeight: "100vh", // Use minHeight for flexibility
        padding: '30px 20px', // More padding
        boxSizing: 'border-box',
        textAlign: 'center', // Center text by default
    },
    logo: {
        width: 'auto',
        height: 'auto',
        maxHeight: "80px", // Smaller logo for this screen
        maxWidth: '70%',
        marginBottom: 30, // Space below logo
        objectFit: "contain",
    },
    title: {
        fontSize: "22px", // Slightly smaller title
        fontWeight: "bold",
        color: '#f0f0f0', // Light text
        marginBottom: "10px",
    },
    description: {
        fontSize: "15px", // Slightly smaller description
        color: "#a0a0b0", // Lighter gray
        textAlign: "center",
        marginBottom: "25px", // More space below description
        maxWidth: "380px", // Limit width
        lineHeight: '1.5',
    },
    input: {
        width: "100%",
        maxWidth: "400px",
        padding: "14px 18px",
        margin: "10px 0",
        border: "1px solid #4a4a6e",
        borderRadius: "8px",
        backgroundColor: "#1a1a2e",
        color: "#f0f0f0",
        fontSize: "16px",
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.3)",
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s ease',
        textAlign: 'left', // Keep input text left-aligned
    },
    // Base button style
    buttonBase: {
        width: "100%",
        maxWidth: "400px",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        padding: "12px",
        borderRadius: "8px",
        fontSize: "16px",
        fontWeight: 'bold',
        border: "none",
        cursor: "pointer",
        transition: 'background-color 0.3s ease, opacity 0.3s ease',
        boxSizing: 'border-box',
    },
    // Primary button (Send Link) style, accepts loading state
    buttonPrimary: (isLoading) => ({
        ...styles.buttonBase,
        backgroundColor: isLoading ? '#5a0ca0' : '#6a0dad', // Theme purple
        color: "white",
        marginTop: 20, // Space above button
        opacity: isLoading ? 0.7 : 1,
        cursor: isLoading ? "not-allowed" : "pointer",
    }),
    linkButton: {
        color: "#a0a0b0",
        background: "none",
        border: "none",
        marginTop: 25, // More space above back link
        fontSize: 14,
        cursor: "pointer",
        padding: '5px',
        textAlign: 'center',
        transition: 'color 0.2s ease',
        display: 'inline-flex', // Align icon and text
        alignItems: 'center',
    },
    loginLink: { // Specific style for the "Entrar" part
        color: "#c0c0ff", // Brighter link color
        fontWeight: "bold",
        textDecoration: 'underline',
        marginLeft: '4px', // Space before link text
    },
    errorBanner: {
        width: '100%',
        maxWidth: "400px",
        backgroundColor: 'rgba(255, 96, 96, 0.1)',
        border: '1px solid rgba(255, 96, 96, 0.3)',
        color: '#ff8080',
        padding: '10px 15px',
        borderRadius: '8px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        marginTop: '10px', // Space above error message
        marginBottom: '5px', // Space below error message
        boxSizing: 'border-box',
    },
    successBanner: { // Style for success message
        width: '100%',
        maxWidth: "400px",
        backgroundColor: 'rgba(80, 200, 120, 0.1)', // Light green background
        border: '1px solid rgba(80, 200, 120, 0.3)',
        color: '#60d090', // Green text
        padding: '15px',
        borderRadius: '8px',
        fontSize: '14px',
        textAlign: 'center',
        marginTop: '10px',
        marginBottom: '15px',
        lineHeight: '1.5',
        boxSizing: 'border-box',
    },
    buttonSpinner: {
        border: '3px solid rgba(255, 255, 255, 0.3)',
        borderLeftColor: '#fff',
        borderRadius: '50%',
        width: '16px',
        height: '16px',
        animation: 'spin 1s linear infinite',
        display: 'inline-block',
    },
    // Optional Full Screen Loading Styles
    // loadingOverlay: {
    //     position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
    //     backgroundColor: "rgba(0, 0, 0, 0.7)", display: "flex",
    //     alignItems: "center", justifyContent: "center", zIndex: 2000,
    // },
    // spinner: { ... same spinner style ... },
};
// --- End Styles ---